import React, { Fragment, useState } from 'react';
import { connect } from 'react-redux';
import { compose } from 'react-apollo';
import { get } from 'lodash';
import { AccessibilityWrapper } from 'legacy-packages/ft-cb';
import SingleAction from 'chameleon/ui-stack/ui/assessments/actions/single-action';
import { submitSessions, unsubmitSessions } from 'legacy-src/queries';
import ConfirmDialog from 'chameleon/ui-stack/ui/modals/confirm-dialog';
import { client } from 'legacy-src/store';
import './SubmitUnsubmit.scss';
import { sendToTelemetry, TLTRY_DEFAULTS } from 'legacy-src/utility/telemetry';
import { TELEMETRY_EVENTS } from 'legacy-src/utility/telemetry';
import InfoMessage from 'chameleon/ui-stack/ui/basic/info-message';
import dayjs from 'dayjs';

export const EXTRA_TEXT_TEST_SELECTOR = 'extra-text';
export const DIALOG_TEXT_TEST_SELECTOR = 'dialog-text';
export const SUBMIT_UNSUBMIT_BUTTON_TEST_SELECTOR = 'submit-unsubmit-button';

const SubmitUnsubmit = ({
  isSubmit = true,
  labelText = null, // e.g. Submit All Unsubmitted
  dialogTitle = null, // e.g. Confirm Submit All
  dialogText = null, // e.g. Submit all unsubmitted assignments?
  onUpdate = () => {},
  name = '',
  assignmentId = null,
  userIds = [],
  propAssignmentId,
  numberOfUnsubmittedAssignments = 1,
  assessmentId,
  classId,
  extraText = null,
  isDueDateInPast,
  isSoftDueDate,
  allowLateSubmissionFF,
}) => {
  const [showConfirm, setShowConfirm] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const label =
    labelText || `${isSubmit ? 'Submit' : 'Unsubmit'} ${name}'s Assignment`;

  const warningBubbleText = allowLateSubmissionFF ? (
    <Fragment>
      This student's due date has passed and the assignment settings do not
      allow for late submissions. To allow this student to continue this
      assignment, unsubmit <strong>and</strong> then change the due date or
      enable late submissions.
    </Fragment>
  ) : (
    <Fragment>
      This student&apos;s due date has passed. To allow this student to continue
      this assignment, unsubmit <strong>and</strong> change the due date.
    </Fragment>
  );

  const Item = (
    <SingleAction
      tabIndex={0}
      onClick={setShowConfirm}
      onKeyDown={(e) => {
        e.keyCode === 13 && setShowConfirm(true);
      }}
      iconClassName="submit-unsubmit-icon"
      iconName={isSubmit ? 'submit' : 'unsubmit'}
      data-test-id={SUBMIT_UNSUBMIT_BUTTON_TEST_SELECTOR}
    >
      <span className="item-text">{label}</span>
    </SingleAction>
  );

  const onSendTelemetryEvent = () => {
    const { importId: teacherId } = window.current_user || {};
    const payload = {
      app: 'assessment_submit_unsubmit',
      event_type: isSubmit
        ? TELEMETRY_EVENTS.teacherSubmitStudentAssignment
        : TELEMETRY_EVENTS.teacherUnSubmitStudentAssignment,
      action_date: dayjs.utc().format(),
      assessment_id: parseInt(assessmentId),
      class_id: parseInt(classId),
      teacher_id: parseInt(teacherId),
      student_count: parseInt(numberOfUnsubmittedAssignments),
      assignment_id: parseInt(assignmentId),
    };
    sendToTelemetry(Object.assign(payload, TLTRY_DEFAULTS[0]));
  };

  const getPrimaryBtnLabel = (isSubmit, isLoading) => {
    if (isLoading && isSubmit) {
      return 'Submitting...';
    } else if (isLoading && !isSubmit) {
      return 'Unsubmitting...';
    } else if (isSubmit) {
      return 'Submit';
    }

    return 'Unsubmit';
  };

  return (
    <Fragment>
      <AccessibilityWrapper>{Item}</AccessibilityWrapper>

      {showConfirm && (
        <ConfirmDialog
          title={dialogTitle || `Confirm ${isSubmit ? 'Submit' : 'Unsubmit'}`}
          isPrimaryBtnDisabled={isLoading}
          contentLabel="Unsaved Changes"
          onClose={() => {
            setShowConfirm(false);
            setIsLoading(false);
          }}
          onSuccess={async () => {
            setIsLoading(true);
            await client.mutate({
              mutation: isSubmit ? submitSessions : unsubmitSessions,
              variables: {
                assignmentId: assignmentId || propAssignmentId,
                userIds: userIds.map((userId) => Number.parseInt(userId, 10)),
              },
              fetchPolicy: 'network-only',
            });

            setIsLoading(false);
            setShowConfirm(false);
            onUpdate();
            onSendTelemetryEvent();
          }}
          aria={{
            live: 'polite',
          }}
          content={{
            btnSecondary: 'Cancel',
            btnPrimary: getPrimaryBtnLabel(isSubmit, isLoading),
          }}
        >
          {!isSubmit && isDueDateInPast && !isSoftDueDate && (
            <InfoMessage
              isWarning
              containerAccessibility={{ 'aria-live': 'polite' }}
            >
              {warningBubbleText}
            </InfoMessage>
          )}

          <p data-test-id={DIALOG_TEXT_TEST_SELECTOR}>
            {dialogText ||
              `Are you sure you want to submit this student’s in-progress assignment?`}
          </p>
          {extraText && isDueDateInPast && (
            <p className="mt-0" data-test-id={EXTRA_TEXT_TEST_SELECTOR}>
              {extraText}
            </p>
          )}
        </ConfirmDialog>
      )}
    </Fragment>
  );
};

const mapStateToProps = (state) => ({
  propAssignmentId: get(state, 'teacherAssignment.assignment.id'),
});

export default compose(connect(mapStateToProps))(SubmitUnsubmit);
